import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import CategoryHero from "@/components/feature/CategoryHero";
import ProductGridCard from "@/components/feature/ProductGridCard";
import CategoryFilters, {
  hasActiveFilters,
  initialFilters,
  priceRanges,
  type FilterState,
} from "./components/CategoryFilters";
import { categories } from "@/mocks/categories";
import { products } from "@/mocks/products";
import type { ColorOption } from "@/types";

type SortKey = "recentes" | "menor" | "maior" | "vendidos";

const sortOptions: { id: SortKey; label: string }[] = [
  { id: "recentes", label: "Mais recentes" },
  { id: "menor", label: "Menor preço" },
  { id: "maior", label: "Maior preço" },
  { id: "vendidos", label: "Mais vendidos" },
];

export default function CategoryPage() {
  const { slug } = useParams();
  const category = categories.find((item) => item.id === slug);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [sort, setSort] = useState<SortKey>("recentes");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const categoryProducts = useMemo(
    () => products.filter((product) => product.categoryId === slug),
    [slug],
  );

  const sizes = useMemo(() => {
    const unique = new Set<string>();
    categoryProducts.forEach((product) => product.sizes.forEach((size) => unique.add(size)));
    return [...unique];
  }, [categoryProducts]);

  const colors = useMemo(() => {
    const map = new Map<string, ColorOption>();
    categoryProducts.forEach((product) => {
      product.colors.forEach((color) => {
        if (!map.has(color.name)) map.set(color.name, color);
      });
    });
    return [...map.values()];
  }, [categoryProducts]);

  const filtered = useMemo(() => {
    let list = [...categoryProducts];
    if (filters.sizes.length) {
      list = list.filter((product) => product.sizes.some((size) => filters.sizes.includes(size)));
    }
    if (filters.colors.length) {
      list = list.filter((product) =>
        product.colors.some((color) => filters.colors.includes(color.name)),
      );
    }
    if (filters.priceRanges.length) {
      list = list.filter((product) => {
        const price = product.promoPrice ?? product.price;
        return priceRanges.some(
          (range) =>
            filters.priceRanges.includes(range.id) && price >= range.min && price <= range.max,
        );
      });
    }
    if (filters.onlyNew) list = list.filter((product) => product.isNew);
    if (filters.onlyBest) list = list.filter((product) => product.tag === "Mais vendido");
    if (filters.onlyAvailable) list = list.filter((product) => product.stock > 0);

    switch (sort) {
      case "menor":
        list.sort((a, b) => (a.promoPrice ?? a.price) - (b.promoPrice ?? b.price));
        break;
      case "maior":
        list.sort((a, b) => (b.promoPrice ?? b.price) - (a.promoPrice ?? a.price));
        break;
      case "vendidos":
        list.sort((a, b) => b.sold - a.sold);
        break;
      default:
        break;
    }
    return list;
  }, [categoryProducts, filters, sort]);

  const active = hasActiveFilters(filters);

  if (!category) {
    return (
      <section className="w-full px-4 py-20 md:px-8 md:py-28">
        <div className="mx-auto flex max-w-xl flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-3xl text-foreground-900">
            Não encontramos esta categoria
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            Talvez o endereço esteja incorreto. Volte para a página inicial e escolha uma categoria.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            Voltar para o início
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div className="w-full">
      {/* Abertura */}
      <CategoryHero category={category}>
        <nav aria-label="Caminho de navegação" className="flex flex-wrap items-center gap-2 text-xs text-background-200">
          <Link to="/" className="cursor-pointer whitespace-nowrap transition-colors hover:text-secondary-300">
            Início
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <span className="whitespace-nowrap text-background-50">{category.name}</span>
        </nav>
        <h1 className="mt-4 font-heading text-4xl font-medium text-background-50 md:text-5xl">
          {category.name}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
          {category.description}
        </p>
      </CategoryHero>

      {/* Barra de controle */}
      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-foreground-700">
              <strong className="font-medium text-foreground-900">{filtered.length}</strong>{" "}
              {filtered.length === 1 ? "peça encontrada" : "peças encontradas"}
            </p>
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200 lg:hidden"
            >
              <i className="ri-equalizer-line text-base" />
              Filtros
              {active ? (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[10px] text-background-50">
                  !
                </span>
              ) : null}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="whitespace-nowrap">Ordenar por</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
              >
                {sortOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <Link
              to="/"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-store-2-line text-base" />
              Ver a loja
            </Link>
          </div>
        </div>
      </section>

      {/* Conteúdo */}
      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-14">
          <div className="flex gap-8">
            <aside className="hidden w-64 shrink-0 lg:block">
              <div className="sticky top-28 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="mb-5 font-heading text-xl text-foreground-900">Filtros</h2>
                <CategoryFilters
                  sizes={sizes}
                  colors={colors}
                  filters={filters}
                  onChange={setFilters}
                  onClear={() => setFilters(initialFilters)}
                />
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-100 py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-search-eye-line text-3xl" />
                  </span>
                  <p className="mt-5 font-heading text-2xl text-foreground-900">
                    Nenhuma peça com esses filtros
                  </p>
                  <p className="mt-2 max-w-sm text-sm text-foreground-600">
                    Tente ajustar as opções ou limpe os filtros para ver todas as peças da categoria.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFilters(initialFilters)}
                    className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    Limpar filtros
                  </button>
                </div>
              ) : (
                <div
                  data-product-shop
                  className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 xl:gap-6"
                >
                  {filtered.map((product, index) => (
                    <Reveal key={product.id} delay={(index % 3) * 70}>
                      <ProductGridCard product={product} />
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Painel mobile de filtros */}
      {filtersOpen ? (
        <div className="fixed inset-0 z-[90] lg:hidden">
          <button
            type="button"
            aria-label="Fechar filtros"
            onClick={() => setFiltersOpen(false)}
            className="absolute inset-0 h-full w-full cursor-pointer bg-foreground-950/50 backdrop-blur-[2px]"
          />
          <div className="ab-modal-in absolute bottom-0 left-0 flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-background-50">
            <header className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h2 className="font-heading text-xl text-foreground-900">Filtros</h2>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="Fechar filtros"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-6">
              <CategoryFilters
                sizes={sizes}
                colors={colors}
                filters={filters}
                onChange={setFilters}
                onClear={() => setFilters(initialFilters)}
              />
            </div>
            <div className="border-t border-background-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                Ver {filtered.length} {filtered.length === 1 ? "peça" : "peças"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}